import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { FtCommentsService } from './ftc-comments.service';

/**
 * Helper function to safely extract plain data from objects (removes circular references)
 */
function extractPlainData(obj: any): any {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map((item: any) => extractPlainData(item));
  }
  const plainObj: any = {};
  for (const key in obj) {
    // Skip AngularJS internal properties
    if (key.indexOf('$$') === 0 || key.indexOf('$') === 0) {
      continue;
    }
    // Skip functions
    if (typeof obj[key] === 'function') {
      continue;
    }
    // Recursively extract plain data
    if (obj[key] != null && typeof obj[key] === 'object') {
      // Check for circular reference indicators
      if (obj[key].$parent !== undefined || obj[key].$root !== undefined ||
          obj[key].$scope !== undefined || obj[key].$$childHead !== undefined) {
        continue;
      }
      plainObj[key] = extractPlainData(obj[key]);
    } else {
      plainObj[key] = obj[key];
    }
  }
  return plainObj;
}

@Component({
    selector: 'gtw-ftc-comments',
    templateUrl: './ftc-comments.component.html',
    styleUrls: ['./ftc-comments.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FtCommentsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // Component properties
  title: string = 'FTC Comments';
  minimise: boolean = false;
  loadingData: boolean = true;
  crudLoading: boolean = false;
  isSaveClicked: boolean = false;
  COMMENTS: string = '';
  allComments: any[] = [];
  is_locked: any;
  ReturnAsFiled: boolean = false;

  baseURLs: any = { 'admin': '', 'api': '', 'custom': '', 'efile': '', 'pdfEngine': '', 'bulkPdfEngine': '', 'base_url': '' };

  filterParams: any = {};

  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  // Row data from AngularJS
  rowData: any = {};

  // Grid data from AngularJS
  gridData: any = null;

  logged_in_user: string = '';
  scenario: string = '';
  jcd_key: string = '';

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private ftCommentsService: FtCommentsService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const fp = normalizeFilterParams(activeScreen);
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const gp = this.gtwGlobal.hydrateGlobalParamsFromContext(activeScreen, fp);
    const plainHeader = extractPlainData({ ...header, globalParams: gp });
    this.globalService = plainHeader;
    this.appService.USER_SETTINGS = plainHeader.userSettingsData ? extractPlainData(plainHeader.userSettingsData) : {};
    this.appService.SERVER_CONFIG = plainHeader.serverConfigData ? extractPlainData(plainHeader.serverConfigData) : {};
    this.GENERAL_CONFIG = plainHeader.generalConfigData ? extractPlainData(plainHeader.generalConfigData) : {};
    if (this.GENERAL_CONFIG && !this.GENERAL_CONFIG.base_url) {
      if (this.GENERAL_CONFIG.baseUrl) {
        this.GENERAL_CONFIG.base_url = this.GENERAL_CONFIG.baseUrl;
      } else if (plainHeader.base_url) {
        this.GENERAL_CONFIG.base_url = plainHeader.base_url;
      }
    }
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    if (this.baseURLs.base_url && this.GENERAL_CONFIG && !this.GENERAL_CONFIG.base_url) {
      this.GENERAL_CONFIG.base_url = this.baseURLs.base_url;
    }
    this.filterParams = extractPlainData(fp);
    const row = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(row) ? (row[0] ? extractPlainData(row[0]) : {}) : extractPlainData(row || {});
    this.gridData = extractPlainData(gridPayloadFromActiveScreen(activeScreen));
  }

  private tryInit(): void {
    if (this.rowData && Object.keys(this.rowData).length > 0 && this.GENERAL_CONFIG) {
      this.init();
    }
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0 && this.GENERAL_CONFIG) {
        this.setupUserData();
        this.checkLockStatus();
        this.loadComments();
      }
    } catch (e) {
      console.error('Error in FTC Comments init:', e);
      this.loadingData = false;
      this.cdr.detectChanges();
    }
  }

  private setupUserData() {
    this.logged_in_user = this.appService.USER_SETTINGS?.user?.sso_id || '';
    this.scenario = this.filterParams?.scenario || this.globalService?.scenario || this.globalService?.globalParams?.scenario || '';
    // jcd_key comes from filterParams (primary) or globalService.globalParams (fallback)
    this.jcd_key = this.filterParams?.jcd_key || this.globalService?.jcd_key || this.globalService?.globalParams?.jcd_key || '';
    
    console.log('setupUserData - jcd_key:', this.jcd_key);
    console.log('setupUserData - filterParams.jcd_key:', this.filterParams?.jcd_key);
    console.log('setupUserData - globalService.globalParams.jcd_key:', this.globalService?.globalParams?.jcd_key);
  }

  checkLockStatus() {
    if (!this.scenario || !this.jcd_key) {
      return;
    }

    // Get baseUrl from multiple sources (matching AngularJS: GENERAL_CONFIG.base_url)
    const baseUrl = this.GENERAL_CONFIG?.base_url || 
                    this.GENERAL_CONFIG?.baseUrl || 
                    this.baseURLs?.base_url ||
                    '';

    console.log('checkLockStatus - baseUrl:', baseUrl);
    console.log('checkLockStatus - GENERAL_CONFIG:', this.GENERAL_CONFIG);
    console.log('checkLockStatus - baseURLs:', this.baseURLs);

    if (!baseUrl || baseUrl.toString().trim() === '' || baseUrl === 'undefined' || baseUrl === 'null') {
      console.warn('Base URL not available for checking lock status');
      return;
    }

    this.ftCommentsService.checkLockStatus(
      baseUrl,
      this.scenario,
      this.jcd_key,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject) {
          this.is_locked = response.jsonObject;
          if (this.is_locked && this.is_locked[0] && this.is_locked[0].LOCK_STATUS == 'Y') {
            this.cancel();
            this._snackBar.open(
              `Tax Return has already been filed for tax year ${this.filterParams.tax_year || ''}.`,
              'Close',
              {
                duration: 4000,
                panelClass: ['error-snackbar']
              }
            );
            return;
          }
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error checking lock status:', error);
      }
    );
  }

  loadComments() {
    if (!this.rowData) {
      console.log('loadComments - No rowData available');
      this.loadingData = false;
      this.cdr.detectChanges();
      return;
    }

    // Use filterParams as primary source (jcd_key and screen_key are added there by AngularJS controller)
    const screen_key = this.filterParams?.screen_key || this.globalService?.screen_key || this.globalService?.globalParams?.screen_key || '';
    const object_id = this.rowData.OBJECT_ID || '';
    const tax_year = this.filterParams?.tax_year || this.globalService?.tax_year || this.globalService?.globalParams?.tax_year || '';
    const scenario = this.filterParams?.scenario || this.globalService?.scenario || this.globalService?.globalParams?.scenario || '';
    const jcd_key = this.filterParams?.jcd_key || this.globalService?.jcd_key || this.globalService?.globalParams?.jcd_key || '';

    // Get baseUrl from multiple sources (matching AngularJS: GENERAL_CONFIG.base_url)
    const baseUrl = this.GENERAL_CONFIG?.base_url || 
                    this.GENERAL_CONFIG?.baseUrl || 
                    this.baseURLs?.base_url ||
                    '';

    console.log('loadComments - baseUrl:', baseUrl);
    console.log('loadComments - params:', { screen_key, object_id, tax_year, scenario, jcd_key });
    console.log('loadComments - globalService.globalParams:', this.globalService?.globalParams);
    console.log('loadComments - filterParams:', this.filterParams);

    if (!baseUrl || baseUrl.toString().trim() === '' || baseUrl === 'undefined' || baseUrl === 'null') {
      console.error('Base URL not available for loading comments');
      this.allComments = [];
      this.loadingData = false;
      this.cdr.detectChanges();
      return;
    }

    this.ftCommentsService.getAllComments(
      baseUrl,
      screen_key,
      object_id,
      tax_year,
      scenario,
      jcd_key,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        console.log('Full response from getAllComments:', response);
        
        // Handle different response structures
        let commentsData = null;
        if (response) {
          // Check if response has jsonObject directly (most common case)
          if (response.jsonObject !== undefined && response.jsonObject !== null) {
            commentsData = response.jsonObject;
          } 
          // Check if response is wrapped in data property
          else if (response.data && response.data.jsonObject !== undefined) {
            commentsData = response.data.jsonObject;
          }
          // Check if response itself is the array
          else if (Array.isArray(response)) {
            commentsData = response;
          }
        }
        
        console.log('Extracted comments data:', commentsData);
        
        if (commentsData && Array.isArray(commentsData) && commentsData.length > 0) {
          // Create new array reference for OnPush change detection
          this.allComments = [...commentsData];
          console.log('allComments set to:', this.allComments);
        } else {
          // Handle case where jsonObject exists but is empty array or null
          this.allComments = [];
          console.log('No comments data found, setting allComments to empty array');
        }
        
        this.loadingData = false;
        this.cdr.markForCheck();
        this.cdr.detectChanges();
        console.log('Change detection triggered, allComments length:', this.allComments.length);
      },
      (error: any) => {
        console.error('Error loading comments:', error);
        this.allComments = [];
        this.loadingData = false;
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      }
    );
  }

  save() {
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }

    if (this.COMMENTS == undefined || this.COMMENTS == null || this.COMMENTS == '') {
      this._snackBar.open('Please enter a valid comments', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (this.COMMENTS.length > 4000) {
      this._snackBar.open('Please comments entered cannot be more than 4000.', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    // Get baseUrl from multiple sources (matching AngularJS: GENERAL_CONFIG.base_url)
    let baseUrl = this.GENERAL_CONFIG?.base_url || 
                  this.GENERAL_CONFIG?.baseUrl || 
                  this.baseURLs?.base_url ||
                  '';
    
    // Clean and validate baseUrl
    baseUrl = baseUrl ? baseUrl.toString().trim() : '';
    
    console.log('save - baseUrl:', baseUrl);
    console.log('save - GENERAL_CONFIG:', this.GENERAL_CONFIG);
    console.log('save - baseURLs:', this.baseURLs);
    
    if (!baseUrl || baseUrl === 'undefined' || baseUrl === 'null' || baseUrl.length === 0) {
      this._snackBar.open('Base URL not configured. Please contact support.', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;

    const returnClobSettingsObj: any = {};
    returnClobSettingsObj['sso_id'] = this.logged_in_user;
    // Use filterParams as primary source (jcd_key and screen_key are added there by AngularJS controller)
    returnClobSettingsObj['jcd_key'] = this.filterParams?.jcd_key || this.globalService?.jcd_key || this.globalService?.globalParams?.jcd_key || '';
    returnClobSettingsObj['scenario'] = this.scenario;
    returnClobSettingsObj['object_id'] = this.rowData.OBJECT_ID;
    returnClobSettingsObj['screen_key'] = this.filterParams?.screen_key || this.globalService?.screen_key || this.globalService?.globalParams?.screen_key || '';
    returnClobSettingsObj['tax_year'] = this.filterParams?.tax_year || this.globalService?.tax_year || this.globalService?.globalParams?.tax_year || '';
    returnClobSettingsObj['comments'] = this.COMMENTS.replace(/'/g, "'");
    returnClobSettingsObj['OPERATION_TYPE'] = "I";
    // Required configuration parameters (from filters)
    returnClobSettingsObj['scenarioCode'] = this.filterParams?.scenarioCode || this.globalService?.scenarioCode || this.globalService?.globalParams?.scenarioCode || 'RAF';
    returnClobSettingsObj['is_issue_key_enabled'] = this.filterParams?.is_issue_key_enabled || this.globalService?.is_issue_key_enabled || this.globalService?.globalParams?.is_issue_key_enabled || 'N';
    
    console.log('save - jcd_key:', returnClobSettingsObj['jcd_key']);
    console.log('save - filterParams.jcd_key:', this.filterParams?.jcd_key);

    const returnObj: any = {};
    returnObj['object_id'] = this.rowData.OBJECT_ID;
    returnObj['comments'] = this.COMMENTS;
    returnObj['OPERATION_TYPE'] = "I";

    const defaultsChangeDetails: any[] = [];
    defaultsChangeDetails.push(returnObj);

    const otherInfo = returnClobSettingsObj;
    const message = "Comment saved successfully";

    console.log('Saving comments with baseUrl:', baseUrl);
    console.log('GENERAL_CONFIG:', this.GENERAL_CONFIG);

    this.ftCommentsService.saveComments(
      baseUrl,
      returnClobSettingsObj,
      defaultsChangeDetails,
      otherInfo,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        // Handle response structure (Angular HttpClient returns body directly, but check for data wrapper)
        const response = result.data || result;
        const errorMessage = response.errorMessage || result.errorMessage;
        
        if (errorMessage && errorMessage !== 'null' && errorMessage !== '') {
          this._snackBar.open(errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open(message, 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });

          // Clear the comment input
          this.COMMENTS = '';
          
          // Reload comments to show the newly saved one
          this.loadComments();

          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  // TrackBy function for *ngFor to improve performance and ensure proper rendering
  trackByComment(index: number, comment: any): any {
    return comment.SORT_ORDER || index;
  }
}

